import type errors from "@chatcode/i18n/catalogs/en/errors";
import { diagnosticCode, diagnosticCodes } from "@chatcode/i18n";

export type ErrorKey = keyof typeof errors;
type SimpleErrorKey = Exclude<ErrorKey, "unknown">;
export type LocalizedError = { key: SimpleErrorKey } | { key: "unknown"; detail: string; context?: SimpleErrorKey };

// Classification uses the original protocol/API values. Only presentation is
// translated, so session recovery and retries never depend on translated text.
const known: Readonly<Record<string, SimpleErrorKey>> = {
  "Unexpected error": "unexpected",
  "Upgrade could not be started.": "voice_upgrade_failed",
  "Recording is too short.": "voice_too_short",
  "Transcription empty.": "voice_empty",
  "Microphone permission denied.": "voice_microphone_denied",
  "No microphone found.": "voice_microphone_missing",
  "Microphone is already in use.": "voice_microphone_busy",
  "Microphone unavailable.": "voice_microphone_unavailable",
  "Transcription failed.": "voice_transcription_failed",
  "Voice transcription is not configured on this deployment.": "voice_not_configured",
  "Sign in and link Telegram before using voice transcription.": "voice_link_required",
  "Voice transcription is available on Starter and higher plans.": "voice_plan_required",
  "invalid email": "invalid_email",
  "bot check required": "bot_check_required",
  "Complete the bot check first.": "bot_check_required",
  "bot check failed": "bot_check_failed",
  "bot check expired": "bot_check_failed",
  "bot check unavailable": "bot_check_failed",
  "Bot check failed. Please retry.": "bot_check_failed",
  "Bot check could not load. Please retry.": "bot_check_failed",
  "email delivery is not configured": "email_unavailable",
  "failed to send sign-in email": "auth_send_failed",
  "gateway not connected": "gateway_offline",
  "session not found": "session_not_found",
  "invalid locale": "preferences_invalid_locale",
  telegram_not_linked: "telegram_not_linked",
  account_mismatch: "account_mismatch",
  oauth_browser_mismatch: "oauth_browser_mismatch",
  preferences_invalid_locale: "preferences_invalid_locale",
};

export function describeError(error: unknown, fallback: SimpleErrorKey = "network"): LocalizedError {
  const body = error && typeof error === "object" && "body" in error ? error.body : null;
  const code = body && typeof body === "object" && "error_code" in body ? body.error_code : undefined;
  if (typeof code === "string" && Object.values(diagnosticCodes).includes(code as SimpleErrorKey)) return { key: code as SimpleErrorKey };
  if (typeof code === "string" && Object.hasOwn(known, code)) return { key: known[code] };
  const detail = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  const diagnostic = diagnosticCode(detail);
  if (diagnostic) return { key: diagnostic };
  // Stored errors retain their raw diagnostics. Rebuild only recognized
  // Chatcode context prefixes when the user changes language.
  for (const prefix of Object.keys(diagnosticCodes).sort((a, b) => b.length - a.length)) {
    if (detail.startsWith(`${prefix}: `)) return { key: "unknown", context: diagnosticCodes[prefix], detail: detail.slice(prefix.length + 2) };
  }
  if (Object.hasOwn(known, detail)) return { key: known[detail] };
  if (/^(?:NetworkError|Failed to fetch|Load failed|Network request failed|fetch failed)/i.test(detail)) return { key: "network" };
  return detail ? { key: "unknown", detail } : { key: fallback };
}
